import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  fetchSourcesStatus, fetchBuilders, runBuilderScript, 
  fetchSchedules, updateSchedule, fetchUsers, addUser, updateUser, deleteUser 
} from '../services/api';
import { ArrowLeft, Play, Save, Trash2, Minus, Plus } from 'lucide-react';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('builders');
  const [builders, setBuilders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [error, setError] = useState('');
  const [scheduleInputs, setScheduleInputs] = useState({});
  
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user_db' });

  const fetchData = useCallback(async () => {
    try {
      setError('');
      
      if (activeTab === 'builders') {
        const [statusRes, buildersRes, schedulesRes] = await Promise.all([
          fetchSourcesStatus().catch(e => { throw e; }),
          fetchBuilders().catch(e => { throw e; }),
          fetchSchedules().catch(e => { throw e; })
        ]);

        const statusMap = statusRes.reduce((acc, curr) => { acc[curr.name] = curr; return acc; }, {});
        
        const correlatedData = buildersRes.builders.map(builderName => {
          const sourceName = Object.keys(statusMap).find(name => 
              name.toLowerCase().replace(/[^a-z0-9]/g, '') === builderName.replace('_builder', '').replace(/_/g, '')
          );
          return {
              builderName,
              sourceName: sourceName || builderName.replace('_builder', '').replace(/_/g, ' '),
              status: sourceName ? statusMap[sourceName] : null
          };
        });

        setBuilders(correlatedData);
        setScheduleInputs(schedulesRes);
      } else {
        const usersRes = await fetchUsers();
        setUsers(usersRes.users);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRunBuilder = async (builderName) => {
    setLoadingStates(prev => ({ ...prev, [builderName]: true }));
    try {
      await runBuilderScript(builderName);
      alert(`Successfully started builder: ${builderName}. Status will refresh shortly.`);
    } catch (e) {
      alert(`Error starting ${builderName}: ${e.message}`);
    } finally {
      setTimeout(() => {
        setLoadingStates(prev => ({ ...prev, [builderName]: false }));
        fetchData(); 
      }, 5000);
    }
  };
  
  const handleInputChange = (builderName, value) => {
    const intValue = value === '' ? '' : parseInt(value, 10);
    if (isNaN(intValue) || intValue < 0) return;
    setScheduleInputs(prev => ({ ...prev, [builderName]: intValue }));
  };

  const handleSaveSchedule = async (builderName) => {
    const interval = scheduleInputs[builderName] || null;
    setLoadingStates(prev => ({ ...prev, [`schedule-${builderName}`]: true }));
    try {
        await updateSchedule(builderName, interval);
        await fetchData(); 
    } catch (e) {
        alert(`Failed to save schedule for ${builderName}: ${e.message}`);
    } finally {
        setLoadingStates(prev => ({ ...prev, [`schedule-${builderName}`]: false }));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await addUser(newUser);
      setNewUser({ username: '', password: '', role: 'user_db' });
      fetchData();
    } catch (e) {
      alert(`Failed to add user: ${e.message}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await deleteUser(userId);
      fetchData();
    } catch (e) {
      alert(`Failed to delete user: ${e.message}`);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateUser(userId, { role: newRole });
      fetchData();
    } catch (e) {
      alert(`Failed to update role: ${e.message}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <div className="min-h-screen font-sans p-4 sm:p-8" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div className="flex flex-col gap-4">
            <Link to="/" className="inline-flex items-center text-sm transition-colors duration-150" style={{ color: 'var(--text-faint)' }}>
              <ArrowLeft size={16} className="mr-1" />
              Return to Search
            </Link>
            <h2 className="text-xl font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              {activeTab === 'builders' ? 'Builder Controls' : 'User Management'}
            </h2>
            <div className="flex space-x-0.5 p-1 rounded-xl w-fit" style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-primary)' }}>
              <button 
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all duration-150 ${activeTab === 'builders' ? 'bg-indigo-500/15 text-indigo-300 shadow-sm' : ''}`}
                style={activeTab !== 'builders' ? { color: 'var(--text-muted)' } : {}}
                onClick={() => setActiveTab('builders')}
              >
                Builders
              </button>
              <button 
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all duration-150 ${activeTab === 'users' ? 'bg-indigo-500/15 text-indigo-300 shadow-sm' : ''}`}
                style={activeTab !== 'users' ? { color: 'var(--text-muted)' } : {}}
                onClick={() => setActiveTab('users')}
              >
                System Users
              </button>
            </div>
          </div>
          {activeTab === 'builders' && (
            <button 
              className="inline-flex items-center gap-2 bg-indigo-500 text-[#f4f4f5] px-4 py-2 rounded-xl font-medium text-sm hover:bg-indigo-400 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/20"
              onClick={() => handleRunBuilder('all')} 
              disabled={loadingStates['all']}
            >
              <Play size={15} />
              {loadingStates['all'] ? 'Running All...' : 'Run All Builders'}
            </button>
          )}
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center justify-between">
            <p className="text-rose-400/90 text-sm">{error}</p>
            <button onClick={() => fetchData()} className="text-sm font-medium text-rose-400 hover:text-rose-300 transition-colors duration-150">Retry</button>
          </div>
        )}

        <div className="rounded-2xl overflow-hidden shadow-sm" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-primary)' }}>
          {activeTab === 'builders' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="uppercase tracking-widest text-[10px]" style={{ backgroundColor: 'var(--bg-elevated)', color: 'var(--text-faint)', borderBottom: '1px solid var(--border-primary)' }}>
                  <tr>
                    <th className="px-6 py-4 font-medium">Source Name</th>
                    <th className="px-6 py-4 font-medium">Last Updated</th>
                    <th className="px-6 py-4 font-medium text-right">File Count</th>
                    <th className="px-6 py-4 font-medium">Schedule (Days)</th>
                    <th className="px-6 py-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {builders.map(({ builderName, sourceName, status }) => (
                    <tr key={builderName} className="hover:bg-indigo-500/[0.03] transition-colors duration-150" style={{ borderBottom: '1px solid var(--border-primary)' }}>
                      <td className="px-6 py-4 font-medium" style={{ color: 'var(--text-primary)' }}>{sourceName}</td>
                      <td className="px-6 py-4 tabular-nums" style={{ color: 'var(--text-muted)' }}>{formatDate(status?.last_updated)}</td>
                      <td className="px-6 py-4 text-right tabular-nums" style={{ color: 'var(--text-muted)' }}>{status?.file_count || 0}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleInputChange(builderName, String(Math.max(0, (parseInt(scheduleInputs[builderName]) || 0) - 1)))}
                            className="p-1 rounded-md transition-all duration-150" style={{ color: 'var(--text-faint)', backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-secondary)' }}
                          >
                            <Minus size={13} />
                          </button>
                          <input
                            type="number"
                            min="0"
                            placeholder="Off"
                            value={scheduleInputs[builderName] || ''}
                            onChange={(e) => handleInputChange(builderName, e.target.value)}
                            className="w-12 text-center rounded-md px-1 py-1 text-sm focus:outline-none focus:shadow-[0_0_0_1px_rgba(99,102,241,0.3)] transition-all duration-200"
                            style={{ backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-secondary)', color: 'var(--text-primary)' }}
                          />
                          <button
                            type="button"
                            onClick={() => handleInputChange(builderName, String((parseInt(scheduleInputs[builderName]) || 0) + 1))}
                            className="p-1 rounded-md transition-all duration-150" style={{ color: 'var(--text-faint)', backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-secondary)' }}
                          >
                            <Plus size={13} />
                          </button>
                          <button
                            onClick={() => handleSaveSchedule(builderName)}
                            disabled={loadingStates[`schedule-${builderName}`]}
                            className="p-1.5 hover:text-indigo-300 hover:bg-indigo-500/10 rounded-lg transition-all duration-150 disabled:opacity-40 ml-1"
                            style={{ color: 'var(--text-faint)' }}
                            title="Save Schedule"
                          >
                            <Save size={14} />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleRunBuilder(builderName)}
                          disabled={loadingStates[builderName]}
                          className="inline-flex items-center gap-1.5 text-xs font-medium bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 px-3 py-1.5 rounded-lg transition-all duration-150 disabled:opacity-40 border border-indigo-500/20"
                        >
                          <Play size={13} />
                          {loadingStates[builderName] ? 'Running...' : 'Run'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {builders.length === 0 && !error && (
                    <tr><td colSpan="5" className="px-6 py-8 text-center" style={{ color: 'var(--text-faint)' }}>No builders found or still loading...</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-8 p-6 rounded-xl" style={{ backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-primary)' }}>
                <h3 className="text-[10px] font-medium mb-4 uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>Add New System User</h3>
                <form onSubmit={handleAddUser} className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                  <div className="flex-1 w-full">
                    <label className="block text-xs mb-1.5 ml-0.5" style={{ color: 'var(--text-faint)' }}>Username</label>
                    <input 
                      type="text" 
                      placeholder="Enter username" 
                      required
                      value={newUser.username} 
                      onChange={e => setNewUser({...newUser, username: e.target.value})}
                      className="w-full rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:shadow-[0_0_0_1px_rgba(99,102,241,0.3)] transition-all duration-200"
                      style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-secondary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <label className="block text-xs mb-1.5 ml-0.5" style={{ color: 'var(--text-faint)' }}>Password</label>
                    <input 
                      type="password" 
                      placeholder="Enter password" 
                      required
                      value={newUser.password} 
                      onChange={e => setNewUser({...newUser, password: e.target.value})}
                      className="w-full rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:shadow-[0_0_0_1px_rgba(99,102,241,0.3)] transition-all duration-200"
                      style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-secondary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <label className="block text-xs mb-1.5 ml-0.5" style={{ color: 'var(--text-faint)' }}>Access Role</label>
                    <select 
                      value={newUser.role} 
                      onChange={e => setNewUser({...newUser, role: e.target.value})}
                      className="w-full rounded-xl px-3.5 py-2.5 text-sm focus:outline-none transition-all duration-200 appearance-none"
                      style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-secondary)', color: 'var(--text-primary)' }}
                    >
                      <option value="user_db">User (DB Search Only)</option>
                      <option value="user_live">User (DB + Live Search)</option>
                      <option value="admin">Administrator (Full Access)</option>
                    </select>
                  </div>
                  <button 
                    type="submit" 
                    className="w-full sm:w-auto bg-indigo-500 text-[#f4f4f5] px-5 py-2.5 rounded-xl font-medium text-sm hover:bg-indigo-400 transition-all duration-150 shadow-lg shadow-indigo-500/20"
                  >
                    Create
                  </button>
                </form>
              </div>

              <div className="overflow-x-auto">
                <h3 className="text-[10px] font-medium mb-4 uppercase tracking-widest pl-2" style={{ color: 'var(--text-faint)' }}>Active User Accounts</h3>
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="uppercase tracking-widest text-[10px]" style={{ backgroundColor: 'var(--bg-elevated)', color: 'var(--text-faint)', border: '1px solid var(--border-primary)' }}>
                    <tr>
                      <th className="px-6 py-4 font-medium rounded-tl-lg">Username</th>
                      <th className="px-6 py-4 font-medium">Access Role</th>
                      <th className="px-6 py-4 font-medium">Created At</th>
                      <th className="px-6 py-4 font-medium">Status</th>
                      <th className="px-6 py-4 font-medium text-right rounded-tr-lg">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="hover:bg-indigo-500/[0.03] transition-colors duration-150" style={{ borderBottom: '1px solid var(--border-primary)' }}>
                        <td className="px-6 py-4 font-medium" style={{ color: 'var(--text-primary)' }}>{u.username}</td>
                        <td className="px-6 py-4">
                          <select 
                            value={u.role} 
                            onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                            className="rounded-lg px-2 py-1 text-sm focus:outline-none transition-all duration-200"
                            style={{ backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-secondary)', color: 'var(--text-secondary)' }}
                          >
                            <option value="user_db">User (DB)</option>
                            <option value="user_live">User (Live)</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 tabular-nums" style={{ color: 'var(--text-faint)' }}>{formatDate(u.created_at)}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] tracking-wide font-medium ${u.is_active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button 
                            onClick={() => handleDeleteUser(u.id)}
                            className="p-1.5 text-rose-400/60 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg transition-all duration-150"
                            title="Delete User"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && !error && (
                      <tr><td colSpan="5" className="px-6 py-8 text-center" style={{ color: 'var(--text-faint)' }}>No users found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;